/**
 * Nhãn / nhóm / ghim / highlight của hội thoại — phần LOGIC thuần (không React, không SDK) để test được.
 * Dữ liệu là `PersonalPrefs` (mã hóa phía client, riêng từng tài khoản); các hàm `mut*` sửa tại chỗ bản nháp mà
 * `client.updatePrefs` đưa vào (áp lại được khi ghi lệch phiên bản), các hàm còn lại chỉ đọc.
 */
import {
  MAX_GROUPS,
  MAX_LABELS,
  MAX_PINNED,
  type ConversationPref,
  type PersonalPrefs,
  type PrefColor,
  type PrefGroup,
  type PrefLabel,
} from '@swchat/protocol';

export const COLOR_HEX: Record<PrefColor, string> = {
  red: '#e5484d',
  orange: '#f76b15',
  yellow: '#d4a017',
  green: '#30a46c',
  teal: '#12a594',
  blue: '#3e63dd',
  purple: '#8e4ec6',
  pink: '#d6409f',
  gray: '#7c8189',
};

// --- Đọc ---

export const prefOf = (prefs: PersonalPrefs, conversationId: string): ConversationPref | undefined => prefs.conversations[conversationId];

export interface Organizable {
  id: string;
}

export interface ConversationSection<T> {
  /** `pinned` | `group:<id>` | `none` — làm khóa React và để lưu trạng thái thu gọn. */
  key: string;
  kind: 'pinned' | 'group' | 'none';
  group?: PrefGroup;
  items: T[];
}

/**
 * Chia danh sách (đã lọc và đã sắp theo hoạt động gần nhất) thành: Đã ghim (theo thứ tự ghim, mới ghim lên đầu),
 * từng nhóm theo thứ tự người dùng sắp, rồi phần còn lại. Hội thoại đã ghim chỉ hiện ở phần Đã ghim (không lặp trong nhóm).
 * Nhóm rỗng vẫn trả về (để người dùng thấy và thả vào) trừ khi `hideEmptyGroups`.
 */
export function organize<T extends Organizable>(list: readonly T[], prefs: PersonalPrefs, opts: { hideEmptyGroups?: boolean } = {}): ConversationSection<T>[] {
  const pinned = list
    .filter((c) => prefOf(prefs, c.id)?.pinned !== undefined)
    .sort((a, b) => (prefOf(prefs, b.id)!.pinned ?? 0) - (prefOf(prefs, a.id)!.pinned ?? 0));
  const pinnedIds = new Set(pinned.map((c) => c.id));
  const groupIds = new Set(prefs.groups.map((g) => g.id));

  const sections: ConversationSection<T>[] = [];
  if (pinned.length) sections.push({ key: 'pinned', kind: 'pinned', items: pinned });
  for (const g of prefs.groups) {
    const items = list.filter((c) => !pinnedIds.has(c.id) && prefOf(prefs, c.id)?.group === g.id);
    if (items.length || !opts.hideEmptyGroups) sections.push({ key: `group:${g.id}`, kind: 'group', group: g, items });
  }
  const rest = list.filter((c) => {
    if (pinnedIds.has(c.id)) return false;
    const g = prefOf(prefs, c.id)?.group;
    return !g || !groupIds.has(g);
  });
  if (rest.length || !sections.length) sections.push({ key: 'none', kind: 'none', items: rest });
  return sections;
}

/** Lọc theo nhãn (id nhãn; '' = không lọc). */
export function filterByLabel<T extends Organizable>(list: readonly T[], prefs: PersonalPrefs, labelId: string): T[] {
  if (!labelId) return [...list];
  return list.filter((c) => prefOf(prefs, c.id)?.labels.includes(labelId));
}

export const labelsOf = (prefs: PersonalPrefs, conversationId: string): PrefLabel[] => {
  const ids = prefOf(prefs, conversationId)?.labels ?? [];
  return ids.map((id) => prefs.labels.find((l) => l.id === id)).filter((l): l is PrefLabel => !!l);
};

export const pinnedCount = (prefs: PersonalPrefs) => Object.values(prefs.conversations).filter((c) => c.pinned !== undefined).length;

// --- Sửa (bản nháp) ---

const newId = () => (globalThis.crypto?.randomUUID?.() ?? `${Date.now()}${Math.random()}`).replace(/-/g, '').slice(0, 12);

function entry(prefs: PersonalPrefs, id: string): ConversationPref {
  return (prefs.conversations[id] ??= { labels: [] });
}

/** Xóa mục rỗng để khối tùy chọn không phình theo số hội thoại đã từng đụng tới. */
function tidy(prefs: PersonalPrefs, id: string) {
  const c = prefs.conversations[id];
  if (c && !c.labels.length && !c.group && c.pinned === undefined && !c.highlight) delete prefs.conversations[id];
}

export class PrefsLimitError extends Error {}

export function mutTogglePin(prefs: PersonalPrefs, id: string, now = Date.now()) {
  const c = entry(prefs, id);
  if (c.pinned !== undefined) delete c.pinned;
  else {
    if (pinnedCount(prefs) >= MAX_PINNED) {
      tidy(prefs, id);
      throw new PrefsLimitError(`Chỉ ghim tối đa ${MAX_PINNED} hội thoại`);
    }
    c.pinned = now;
  }
  tidy(prefs, id);
}

export function mutSetHighlight(prefs: PersonalPrefs, id: string, color: PrefColor | undefined) {
  const c = entry(prefs, id);
  if (color) c.highlight = color;
  else delete c.highlight;
  tidy(prefs, id);
}

export function mutToggleLabel(prefs: PersonalPrefs, id: string, labelId: string) {
  if (!prefs.labels.some((l) => l.id === labelId)) return;
  const c = entry(prefs, id);
  c.labels = c.labels.includes(labelId) ? c.labels.filter((l) => l !== labelId) : [...c.labels, labelId].slice(0, 10);
  tidy(prefs, id);
}

export function mutSetGroup(prefs: PersonalPrefs, id: string, groupId: string | undefined) {
  const c = entry(prefs, id);
  if (groupId && prefs.groups.some((g) => g.id === groupId)) c.group = groupId;
  else delete c.group;
  tidy(prefs, id);
}

const normName = (s: string) => s.trim().replace(/\s+/g, ' ');

export function mutAddLabel(prefs: PersonalPrefs, name: string, color: PrefColor): string {
  const n = normName(name);
  if (!n) throw new PrefsLimitError('Tên nhãn không được để trống');
  if (prefs.labels.length >= MAX_LABELS) throw new PrefsLimitError(`Tối đa ${MAX_LABELS} nhãn`);
  if (prefs.labels.some((l) => l.name.toLowerCase() === n.toLowerCase())) throw new PrefsLimitError('Đã có nhãn trùng tên');
  const id = newId();
  prefs.labels.push({ id, name: n.slice(0, 30), color });
  return id;
}

export function mutEditLabel(prefs: PersonalPrefs, id: string, patch: { name?: string; color?: PrefColor }) {
  const l = prefs.labels.find((x) => x.id === id);
  if (!l) return;
  if (patch.name !== undefined) {
    const n = normName(patch.name);
    if (!n) throw new PrefsLimitError('Tên nhãn không được để trống');
    if (prefs.labels.some((x) => x.id !== id && x.name.toLowerCase() === n.toLowerCase())) throw new PrefsLimitError('Đã có nhãn trùng tên');
    l.name = n.slice(0, 30);
  }
  if (patch.color) l.color = patch.color;
}

export function mutDeleteLabel(prefs: PersonalPrefs, id: string) {
  prefs.labels = prefs.labels.filter((l) => l.id !== id);
  for (const cid of Object.keys(prefs.conversations)) {
    const c = prefs.conversations[cid]!;
    c.labels = c.labels.filter((l) => l !== id);
    tidy(prefs, cid);
  }
}

export function mutAddGroup(prefs: PersonalPrefs, name: string): string {
  const n = normName(name);
  if (!n) throw new PrefsLimitError('Tên nhóm không được để trống');
  if (prefs.groups.length >= MAX_GROUPS) throw new PrefsLimitError(`Tối đa ${MAX_GROUPS} nhóm`);
  if (prefs.groups.some((g) => g.name.toLowerCase() === n.toLowerCase())) throw new PrefsLimitError('Đã có nhóm trùng tên');
  const id = newId();
  prefs.groups.push({ id, name: n.slice(0, 40), collapsed: false });
  return id;
}

export function mutRenameGroup(prefs: PersonalPrefs, id: string, name: string) {
  const g = prefs.groups.find((x) => x.id === id);
  if (!g) return;
  const n = normName(name);
  if (!n) throw new PrefsLimitError('Tên nhóm không được để trống');
  if (prefs.groups.some((x) => x.id !== id && x.name.toLowerCase() === n.toLowerCase())) throw new PrefsLimitError('Đã có nhóm trùng tên');
  g.name = n.slice(0, 40);
}

export function mutDeleteGroup(prefs: PersonalPrefs, id: string) {
  prefs.groups = prefs.groups.filter((g) => g.id !== id);
  for (const cid of Object.keys(prefs.conversations)) {
    const c = prefs.conversations[cid]!;
    if (c.group === id) delete c.group;
    tidy(prefs, cid);
  }
}

export function mutMoveGroup(prefs: PersonalPrefs, id: string, delta: -1 | 1) {
  const i = prefs.groups.findIndex((g) => g.id === id);
  const j = i + delta;
  if (i < 0 || j < 0 || j >= prefs.groups.length) return;
  const [g] = prefs.groups.splice(i, 1);
  prefs.groups.splice(j, 0, g!);
}

export function mutToggleGroupCollapsed(prefs: PersonalPrefs, id: string) {
  const g = prefs.groups.find((x) => x.id === id);
  if (g) g.collapsed = !g.collapsed;
}
